import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TableSortLabel from '@mui/material/TableSortLabel';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Search, Edit as EditIcon, Delete as DeleteIcon, Play as StatoInizialeLogoIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

function AttivitaStatoInizialeInner() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [dailyTarget, setDailyTarget] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [users, setUsers] = useState([]);
  const [targetAssignments, setTargetAssignments] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignCurrentId, setAssignCurrentId] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignStatoId, setAssignStatoId] = useState('');
  const [assignDailyTarget, setAssignDailyTarget] = useState('');
  const [assignValidFrom, setAssignValidFrom] = useState('');
  const [assignValidTo, setAssignValidTo] = useState('');
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [activeTab, setActiveTab] = useState('base');
  const [assignPage, setAssignPage] = useState(0);
  const [assignRowsPerPage, setAssignRowsPerPage] = useState(10);
  const [assignOrder, setAssignOrder] = useState('asc');
  const [assignOrderBy, setAssignOrderBy] = useState('user_name');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/attivita/stato-iniziale');
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users', { params: { page: 1, limit: 1000, sort: 'name', order: 'ASC' } });
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      setUsers(list);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento utenti');
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await api.get('/attivita/stato-iniziale-targets');
      setTargetAssignments(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento obiettivi per utente');
    }
  };

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchAssignments();
  }, []);

  const filtered = rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sorted = [...filtered].sort((a, b) => {
    const valueA = a[orderBy];
    const valueB = b[orderBy];
    if (valueA === null || valueA === undefined) return 1;
    if (valueB === null || valueB === undefined) return -1;
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();
    if (strA < strB) return order === 'asc' ? -1 : 1;
    if (strA > strB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentId(row.id);
      setName(row.name || '');
      setColor(row.color || '');
      setDailyTarget(row.daily_target != null ? String(row.daily_target) : '');
      setBlocking(Boolean(row.blocking));
    } else {
      setCurrentId(null);
      setName('');
      setColor('');
      setDailyTarget('');
      setBlocking(false);
    }
    setDialogOpen(true);
  };
  const handleCloseDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    try {
      if (!name.trim()) {
        toast.error('Nome obbligatorio');
        return;
      }
      const payload = {
        name,
        color: color || null,
        daily_target: dailyTarget !== '' ? parseInt(dailyTarget, 10) : null,
        blocking: blocking ? 1 : 0,
      };
      if (currentId) {
        await api.put(`/attivita/stato-iniziale/${currentId}`, payload);
        toast.success('Aggiornato');
      } else {
        await api.post('/attivita/stato-iniziale', payload);
        toast.success('Creato');
      }
      handleCloseDialog();
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try {
      await api.delete(`/attivita/stato-iniziale/${id}`);
      toast.success('Eliminato');
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Errore eliminazione');
    }
  };

  const openAssignDialog = (assignment = null) => {
    if (assignment) {
      setAssignCurrentId(assignment.id);
      setAssignUserId(assignment.user_id ? String(assignment.user_id) : 'global');
      setAssignStatoId(String(assignment.stato_iniziale_id));
      setAssignDailyTarget(String(assignment.daily_target ?? ''));
      setAssignValidFrom(String(assignment.valid_from || '').slice(0, 10));
      setAssignValidTo(assignment.valid_to ? String(assignment.valid_to).slice(0, 10) : '');
    } else {
      setAssignCurrentId(null);
      setAssignUserId('global');
      setAssignStatoId('');
      setAssignDailyTarget('');
      setAssignValidFrom('');
      setAssignValidTo('');
    }
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => setAssignDialogOpen(false);

  const handleSaveAssignment = async () => {
    try {
      if (!assignStatoId || assignDailyTarget === '' || !assignValidFrom) {
        toast.error('Compila stato, obiettivo e data inizio');
        return;
      }
      const payload = {
        user_id: assignUserId === 'global' ? null : Number(assignUserId),
        stato_iniziale_id: Number(assignStatoId),
        daily_target: Number(assignDailyTarget),
        valid_from: assignValidFrom,
        valid_to: assignValidTo || null
      };
      if (assignCurrentId) {
        await api.put(`/attivita/stato-iniziale-targets/${assignCurrentId}`, payload);
        toast.success('Obiettivo utente aggiornato');
      } else {
        await api.post('/attivita/stato-iniziale-targets', payload);
        toast.success('Obiettivo utente creato');
      }
      closeAssignDialog();
      fetchAssignments();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Errore salvataggio obiettivo utente');
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Eliminare questo obiettivo per utente?')) return;
    try {
      await api.delete(`/attivita/stato-iniziale-targets/${id}`);
      toast.success('Obiettivo utente eliminato');
      fetchAssignments();
    } catch (e) {
      console.error(e);
      toast.error('Errore eliminazione obiettivo utente');
    }
  };

  const filteredAssignments = useMemo(() => {
    const q = assignmentSearch.trim().toLowerCase();
    if (!q) return targetAssignments;
    return targetAssignments.filter((a) => {
      const userName = (a.user_name || 'Globale').toLowerCase();
      const statoName = (a.stato_iniziale_name || '').toLowerCase();
      return userName.includes(q) || statoName.includes(q);
    });
  }, [targetAssignments, assignmentSearch]);

  const handleAssignRequestSort = (property) => {
    const isAsc = assignOrderBy === property && assignOrder === 'asc';
    setAssignOrder(isAsc ? 'desc' : 'asc');
    setAssignOrderBy(property);
  };

  const sortedAssignments = useMemo(() => {
    const arr = [...filteredAssignments];
    arr.sort((a, b) => {
      const valueA = a[assignOrderBy];
      const valueB = b[assignOrderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return assignOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return assignOrder === 'asc' ? -1 : 1;
      if (strA > strB) return assignOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredAssignments, assignOrder, assignOrderBy]);

  const userOptions = useMemo(
    () => [
      { value: 'global', label: 'Globale (tutti)' },
      ...users.map((u) => ({ value: String(u.id), label: u.name }))
    ],
    [users]
  );

  const statoOptions = useMemo(
    () => rows.map((s) => ({ value: String(s.id), label: s.name })),
    [rows]
  );

  const selectedUserOption = userOptions.find((o) => o.value === assignUserId) || null;
  const selectedStatoOption = statoOptions.find((o) => o.value === assignStatoId) || null;

  const formatDateIt = (value) => {
    if (!value) return '';
    const iso = String(value).slice(0, 10);
    const parts = iso.split('-');
    if (parts.length !== 3) return String(value);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <StatoInizialeLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Stato Iniziale
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configurazione degli stati iniziali e degli obiettivi per utente
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            if (activeTab === 'base') {
              handleOpenDialog();
            } else {
              openAssignDialog();
            }
          }}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          {activeTab === 'base' ? 'Nuovo' : 'Nuovo obiettivo utente'}
        </Button>
      </Box>

      <StyledCard sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{ px: 2, pt: 1 }}
        >
          <Tab value="base" label="Base" />
          <Tab value="user-targets" label="Obiettivi per utente" />
        </Tabs>
      </StyledCard>

      {activeTab === 'base' && (
        <StyledCard>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={orderBy === 'id' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>Colore</TableCell>
                <TableCell align="center">Obiettivo giornaliero</TableCell>
                <TableCell align="center">Bloccante</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Caricamento...</TableCell>
                </TableRow>
              ) : (
                sorted
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.name}</TableCell>
                      <TableCell>
                        {row.color ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: row.color, border: '1px solid rgba(0,0,0,0.2)' }} />
                            <Typography variant="caption" color="text.secondary">{row.color}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.daily_target != null ? row.daily_target : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.blocking ? 'Sì' : 'No'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary">
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      )}

      {activeTab === 'user-targets' && (
      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Obiettivi per utente con validità</Typography>
            <Typography variant="body2" color="text.secondary">
              Priorità: obiettivo utente attivo nel periodo, altrimenti obiettivo globale.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={assignmentSearch}
            onChange={(e) => setAssignmentSearch(e.target.value)}
            placeholder="Cerca per utente o stato..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 360 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={assignOrderBy === 'user_name' ? assignOrder : false}>
                  <TableSortLabel
                    active={assignOrderBy === 'user_name'}
                    direction={assignOrderBy === 'user_name' ? assignOrder : 'asc'}
                    onClick={() => handleAssignRequestSort('user_name')}
                  >
                    Utente
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={assignOrderBy === 'stato_iniziale_name' ? assignOrder : false}>
                  <TableSortLabel
                    active={assignOrderBy === 'stato_iniziale_name'}
                    direction={assignOrderBy === 'stato_iniziale_name' ? assignOrder : 'asc'}
                    onClick={() => handleAssignRequestSort('stato_iniziale_name')}
                  >
                    Stato iniziale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sortDirection={assignOrderBy === 'daily_target' ? assignOrder : false}>
                  <TableSortLabel
                    active={assignOrderBy === 'daily_target'}
                    direction={assignOrderBy === 'daily_target' ? assignOrder : 'asc'}
                    onClick={() => handleAssignRequestSort('daily_target')}
                  >
                    Obiettivo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sortDirection={assignOrderBy === 'valid_from' ? assignOrder : false}>
                  <TableSortLabel
                    active={assignOrderBy === 'valid_from'}
                    direction={assignOrderBy === 'valid_from' ? assignOrder : 'asc'}
                    onClick={() => handleAssignRequestSort('valid_from')}
                  >
                    Dal
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sortDirection={assignOrderBy === 'valid_to' ? assignOrder : false}>
                  <TableSortLabel
                    active={assignOrderBy === 'valid_to'}
                    direction={assignOrderBy === 'valid_to' ? assignOrder : 'asc'}
                    onClick={() => handleAssignRequestSort('valid_to')}
                  >
                    Al
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Nessun obiettivo per utente configurato</TableCell>
                </TableRow>
              ) : (
                sortedAssignments
                  .slice(assignPage * assignRowsPerPage, assignPage * assignRowsPerPage + assignRowsPerPage)
                  .map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.user_name || 'Globale'}</TableCell>
                    <TableCell>{a.stato_iniziale_name}</TableCell>
                    <TableCell align="center">{a.daily_target}</TableCell>
                    <TableCell align="center">{formatDateIt(a.valid_from)}</TableCell>
                    <TableCell align="center">{a.valid_to ? formatDateIt(a.valid_to) : '∞'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openAssignDialog(a)} color="primary">
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteAssignment(a.id)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredAssignments.length}
          rowsPerPage={assignRowsPerPage}
          page={assignPage}
          onPageChange={(e, p) => setAssignPage(p)}
          onRowsPerPageChange={(e) => { setAssignRowsPerPage(parseInt(e.target.value, 10)); setAssignPage(0); }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{currentId ? 'Modifica' : 'Nuovo'} Stato Iniziale</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Colore (opzionale)"
              type="color"
              value={color || '#000000'}
              onChange={(e) => setColor(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: color || 'transparent', border: '1px solid rgba(0,0,0,0.2)' }} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Obiettivo giornaliero (numero pratiche)"
              type="number"
              value={dailyTarget}
              onChange={(e) => setDailyTarget(e.target.value)}
              InputProps={{ inputProps: { min: 0 } }}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={blocking}
                  onChange={(e) => setBlocking(e.target.checked)}
                  color="primary"
                />
              }
              label="Bloccante (minimo da raggiungere)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{assignCurrentId ? 'Modifica' : 'Nuovo'} obiettivo per utente</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={userOptions}
                value={selectedUserOption}
                onChange={(event, newValue) => setAssignUserId(newValue?.value || 'global')}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField {...params} label="Utente" placeholder="Cerca utente..." />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={statoOptions}
                value={selectedStatoOption}
                onChange={(event, newValue) => setAssignStatoId(newValue?.value || '')}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField {...params} label="Stato iniziale" placeholder="Cerca stato..." />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Obiettivo"
                type="number"
                value={assignDailyTarget}
                onChange={(e) => setAssignDailyTarget(e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Valido dal"
                type="date"
                value={assignValidFrom}
                onChange={(e) => setAssignValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Valido al"
                type="date"
                value={assignValidTo}
                onChange={(e) => setAssignValidTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog}>Annulla</Button>
          <Button onClick={handleSaveAssignment} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AttivitaStatoIniziale() {
  const { user } = useAuth();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AttivitaStatoInizialeInner />;
}
